import { useGridFilter } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useState, type FC } from 'react';

import type { ColDef, GridApi, IRowNode } from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { Checkbox, Input, typographyVariants } from '@mint-lib/tw-components';

import type { ApprovalTableItemData } from '../../../types/approversTable.js';
import {
  getEntityLabels,
  getUniqueEntityLabels,
} from '../entityFilterHelpers.js';

export type EntitySetFilterModel = {
  values: string[];
};

type EntitySetFilterProps = {
  api: GridApi<ApprovalTableItemData>;
  column: { getColDef(): ColDef<ApprovalTableItemData> };
  model: EntitySetFilterModel | null;
  onModelChange: (model: EntitySetFilterModel | null) => void;
  doesRowPassOtherFilter: (node: IRowNode<ApprovalTableItemData>) => boolean;
};

function getVisibleLabels(
  api: GridApi<ApprovalTableItemData>,
  field: 'requesters' | 'approvers',
  doesRowPassOtherFilter: (node: IRowNode<ApprovalTableItemData>) => boolean,
): string[] {
  const visible: ApprovalTableItemData[] = [];
  api.forEachNode((node) => {
    if (node.data && doesRowPassOtherFilter(node)) {
      visible.push(node.data);
    }
  });
  return getUniqueEntityLabels(visible, field);
}

const EntitySetFilter: FC<EntitySetFilterProps> = ({
  api,
  column,
  model,
  onModelChange,
  doesRowPassOtherFilter,
}) => {
  const { t } = useTranslation('@myn/approval_v2');

  const field = (column.getColDef().filterParams?.field ??
    column.getColDef().field) as 'requesters' | 'approvers';

  const [visibleLabels, setVisibleLabels] = useState<string[]>(() =>
    getVisibleLabels(api, field, doesRowPassOtherFilter),
  );

  const [searchTerm, setSearchTerm] = useState('');

  const isFilterInactive = model == null;

  const selectedSet = useMemo(() => {
    return new Set(isFilterInactive ? visibleLabels : model.values);
  }, [isFilterInactive, model, visibleLabels]);

  const refreshLabels = useCallback(() => {
    setVisibleLabels((prev) => {
      const fresh = getVisibleLabels(api, field, doesRowPassOtherFilter);
      if (
        fresh.length === prev.length &&
        fresh.every((v, i) => v === prev[i])
      ) {
        return prev;
      }
      return fresh;
    });
  }, [api, field, doesRowPassOtherFilter]);

  useGridFilter({
    doesFilterPass: useCallback(
      (params: {
        node: IRowNode<ApprovalTableItemData>;
        data: ApprovalTableItemData;
      }) => {
        if (isFilterInactive) return true;
        const labels = getEntityLabels(params.data, field);
        const selected = model?.values ?? [];
        return labels.some((l) => selected.includes(l));
      },
      [field, isFilterInactive, model?.values],
    ),
    afterGuiAttached: refreshLabels,
    onNewRowsLoaded: refreshLabels,
    onAnyFilterChanged: refreshLabels,
  });

  const handleToggle = useCallback(
    (label: string, checked: boolean) => {
      const next = new Set(selectedSet);

      checked ? next.add(label) : next.delete(label);

      onModelChange(
        next.size === visibleLabels.length
          ? null
          : {
              values: Array.from(next).sort((a, b) => a.localeCompare(b)),
            },
      );
    },
    [onModelChange, selectedSet, visibleLabels.length],
  );

  const filteredLabels = useMemo(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return visibleLabels;
    const term = trimmed.toLowerCase();
    return visibleLabels.filter((label) => label.toLowerCase().includes(term));
  }, [visibleLabels, searchTerm]);

  const selectAllState = useMemo<boolean | 'indeterminate'>(() => {
    if (!filteredLabels.length) {
      return false;
    }
    const count = filteredLabels.filter((l) => selectedSet.has(l)).length;
    if (count === 0) {
      return false;
    }
    return count === filteredLabels.length ? true : 'indeterminate';
  }, [filteredLabels, selectedSet]);

  const handleSelectAll = useCallback(() => {
    const next = new Set(selectedSet);

    for (const label of filteredLabels) {
      selectAllState ? next.delete(label) : next.add(label);
    }

    next.size === visibleLabels.length
      ? onModelChange(null)
      : onModelChange({
          values: Array.from(next).sort((a, b) => a.localeCompare(b)),
        });
  }, [
    selectAllState,
    selectedSet,
    filteredLabels,
    visibleLabels.length,
    onModelChange,
  ]);

  return (
    <div className="flex min-w-[200px] flex-col p-3">
      <Input
        size="small"
        placeholder={t('Search...')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-2"
      />
      <label className="flex cursor-pointer items-center gap-2 py-1.5">
        <Checkbox
          checked={selectAllState}
          onCheckedChange={handleSelectAll}
          aria-label={t('Select all')}
        />
        <span
          className={typographyVariants({
            variant: 'bodyShort01',
          })}
        >
          {t('Select all')}
        </span>
      </label>
      <div className="flex h-40 flex-col overflow-auto">
        {filteredLabels.length === 0 && searchTerm.trim() ? (
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              className: 'py-1.5',
            })}
          >
            {t('No matches')}
          </p>
        ) : (
          filteredLabels.map((label) => (
            <label
              key={label}
              className="flex cursor-pointer items-center gap-2 py-1.5 hover:bg-ui-secondary/50"
            >
              <Checkbox
                checked={selectedSet.has(label)}
                onCheckedChange={(checked) =>
                  handleToggle(label, Boolean(checked))
                }
                aria-label={label}
              />
              <span
                className={typographyVariants({
                  variant: 'bodyShort01',
                  className: 'py-0.5',
                })}
              >
                {label}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export default EntitySetFilter;
